import React from "react";
import { AbsoluteFill, useCurrentFrame } from "remotion";

interface TechBackgroundProps {
  theme?: "dark-emerald" | "warm-cream" | "cyber-navy" | "midnight";
  accentColor?: string;
  enableGrid?: boolean;
}

export const TechBackground: React.FC<TechBackgroundProps> = ({
  theme = "dark-emerald",
  accentColor = "#10B981",
  enableGrid = true,
}) => {
  const frame = useCurrentFrame();

  const themes = {
    "dark-emerald": {
      base: "#0A140E",
      glow1: "rgba(16, 185, 129, 0.22)",
      glow2: "rgba(20, 184, 166, 0.12)",
      dotColor: "rgba(255, 255, 255, 0.06)",
    },
    "warm-cream": {
      base: "#F8F6F0",
      glow1: "rgba(212, 163, 70, 0.18)",
      glow2: "rgba(27, 59, 43, 0.08)",
      dotColor: "rgba(0, 0, 0, 0.04)",
    },
    "cyber-navy": {
      base: "#0A0F1D",
      glow1: "rgba(59, 130, 246, 0.22)",
      glow2: "rgba(139, 92, 246, 0.15)",
      dotColor: "rgba(255, 255, 255, 0.06)",
    },
    midnight: {
      base: "#08080C",
      glow1: "rgba(244, 63, 94, 0.18)",
      glow2: "rgba(168, 85, 247, 0.14)",
      dotColor: "rgba(255, 255, 255, 0.05)",
    },
  };

  const currentTheme = themes[theme] || themes["dark-emerald"];

  // Drift animations
  const driftX = (frame * 0.4) % 36;
  const driftY = (frame * 0.4) % 36;

  // Pulse animations for radial lights
  const pulseScale1 = 1 + Math.sin(frame / 45) * 0.08;
  const pulseScale2 = 1 + Math.cos(frame / 50) * 0.1;
  const pulseOpacity1 = 0.8 + Math.sin(frame / 35) * 0.2;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: currentTheme.base,
        overflow: "hidden",
      }}
    >
      {/* 1. Dotted Matrix Pattern */}
      {enableGrid && (
        <div
          style={{
            position: "absolute",
            inset: -40,
            backgroundImage: `radial-gradient(circle, ${currentTheme.dotColor} 1.5px, transparent 1.5px)`,
            backgroundSize: "36px 36px",
            backgroundPosition: `${driftX}px ${driftY}px`,
            pointerEvents: "none",
          }}
        />
      )}

      {/* 2. Top-Center Breathing Spotlight Orb */}
      <div
        style={{
          position: "absolute",
          top: "-15%",
          left: "20%",
          width: "60%",
          height: "55%",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${currentTheme.glow1} 0%, transparent 70%)`,
          filter: "blur(60px)",
          transform: `scale(${pulseScale1})`,
          opacity: pulseOpacity1,
          pointerEvents: "none",
        }}
      />

      {/* 3. Bottom-Right Ambient Rim Light */}
      <div
        style={{
          position: "absolute",
          bottom: "-10%",
          right: "-10%",
          width: "55%",
          height: "45%",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${currentTheme.glow2} 0%, transparent 75%)`,
          filter: "blur(70px)",
          transform: `scale(${pulseScale2})`,
          pointerEvents: "none",
        }}
      />

      {/* 4. Subtle Film Vignette */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, transparent 65%, rgba(0, 0, 0, 0.4) 100%)",
          pointerEvents: "none",
        }}
      />
    </AbsoluteFill>
  );
};
